import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import moment from "moment";

import { get_date } from "../generalComponets";
import { AccountService, CRMService, FinanceService, ProjectService } from "../../services";
import { incomeValidationRules } from "../../pages/client/validation";
import { ActionButton, InputField, SelectField } from "../v3";
import { ENABLE_CLIENT_INCOME_BACKDATE_ENTRY, SourceEnum, VIEW_ACCOUNT, SELECT_OFFICE_ACCOUNT_IN_ENTRIES } from "../../constants";
import { useHasPermission } from "../../utils";
import { markTableReset } from "../../utils/tableStateStorage";

export const AddEditIncomeForm = () => {
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectStage, setSelectedProjectStage] = useState(null);
  const [stageIds, setStageIds] = useState([]);
  const [projectStages, setProjectStages] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [incomeDetails, setIncomeDetails] = useState({
    amount: undefined,
    date: get_date(Date.now(), "YYYY-MM-DD"),
    categoryId: 1,
    description: undefined,
    projectId: null,
    projectStageId: undefined,
    sourceType: SourceEnum.clients,
    sourceId: undefined,
    status: "COMPLETED",
    toAccountId: undefined,
  });

  const { user: currentUser } = useSelector((state) => state.user);
  const { ownerUserId } = currentUser.company;
  const hasViewAccount = useHasPermission(VIEW_ACCOUNT);
  const enableBackDate = useHasPermission(ENABLE_CLIENT_INCOME_BACKDATE_ENTRY);
  const hasSelectOfficeAccount = useHasPermission(SELECT_OFFICE_ACCOUNT_IN_ENTRIES);
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("clientId");
  const projectId = searchParams.get("projectId");
  const accountType = searchParams.get("accountType");
  const { incomeId } = useParams();

  useEffect(() => {
    if (incomeId) {
      fetchIncomeDetails(incomeId);
    }
    fetchData();
  }, []);

  const stageFilter = useMemo(
    () => ({
      ids: stageIds,
      sortBy: "name",
      sortOrder: "asc",
    }),
    [stageIds],
  );

  const fetchData = async () => {
    try {
      if (clientId) {
        const { data: client } = await CRMService.getClientById(clientId);
        setSelectedClient(client);
        setIncomeDetails((prev) => ({
          ...prev,
          sourceId: Number(clientId),
        }));
      }

      if (projectId) {
        const { data: project } = await ProjectService.getProjectById(projectId);
        handleProjectChange(project);

        if (!clientId && project) {
          if (project.client) {
            setSelectedClient(project.client);
            setIncomeDetails((prev) => ({
              ...prev,
              sourceId: project.client.id,
            }));
          } else if (project.clientId) {
            const { data: client } = await CRMService.getClientById(project.clientId);
            setSelectedClient(client);
            setIncomeDetails((prev) => ({
              ...prev,
              sourceId: Number(project.clientId),
            }));
          }
        }
      }

      if (accountType === "office" && !incomeId) {
        setSelectedProject({ id: "office", name: "Company/Office" });
        setIncomeDetails((prev) => ({ ...prev, projectId: "office" }));
      }

      const { data: accounts } = await AccountService.getAdminAndUserAccounts({
        hasViewAccount,
        user: currentUser,
        companyAdminId: ownerUserId,
      });
      const userAccount = currentUser.accounts[0];
      setSelectedAccount(userAccount);
      setIncomeDetails((prev) => ({
        ...prev,
        toAccountId: userAccount?.id,
      }));
    } catch (error) {
      toast.error("Failed to fetch clients and accounts data");
      console.error("🚀 ~ fetchData ~ error:", error);
    }
  };

  const fetchIncomeDetails = async (id) => {
    try {
      const { data } = await FinanceService.getIncomeById(id);
      setSelectedClient(data.source);
      setSelectedProject(data.project || (data.projectId === null ? { id: "office", name: "Company/Office" } : null));

      setIncomeDetails({
        amount: Number(data.amount),
        date: moment(data.date).format("YYYY-MM-DD"),
        categoryId: data.categoryId,
        description: data.description,
        projectId: data.projectId === null ? "office" : data.projectId,
        sourceType: data.sourceType,
        sourceId: data.sourceId,
        status: data.status,
      });
      // fetch project stages
      if (data?.projectId && data?.projectId !== "office") fetchProjectStages(data?.projectId);

      // fetch stages
      if (data?.projectStageId) {
        try {
          const { data: stage } = await ProjectService.getStageById(data?.projectStage?.stageId);
          setSelectedProjectStage(stage);
        } catch (error) {
          toast.error("Failed to fetch stage details");
          console.error("🚀 ~ fetchData ~ error:", error);
        }
      }
    } catch (error) {
      toast.error("Failed to fetch income details");
      console.error("🚀 ~ fetchIncomeDetails ~ error:", error);
    }
  };

  const fetchProjectStages = async (projectId) => {
    try {
      const { data: first } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: 10,
      });

      const { data } = await ProjectService.getProjectStages({
        projectId,
        page: 1,
        limit: first.count,
      });

      const stages = [...new Set(data.rows.map((item) => item.stageId))];

      setStageIds(stages.join(","));
      setProjectStages(data.rows);
    } catch (error) {
      console.error("🚀 ~ fetchProjectStages ~ error:", error);
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    setIncomeDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClientChange = (selected) => {
    setSelectedClient(selected);

    if (accountType !== "office" && incomeDetails.projectId !== "office") {
      setSelectedProject(null);
      setSelectedProjectStage(null);
      setIncomeDetails((prev) => ({
        ...prev,
        sourceId: selected?.id,
        projectId: null,
        projectStageId: null,
      }));
    } else {
      setIncomeDetails((prev) => ({
        ...prev,
        sourceId: selected?.id,
      }));
    }
  };

  const handleProjectChange = (selected) => {
    setStageIds([]);
    setSelectedProjectStage(null);
    setSelectedProject(selected);
    setIncomeDetails((prev) => ({ ...prev, projectId: selected?.id, projectStageId: null }));
    if (selected?.id && selected?.id !== "office") fetchProjectStages(selected.id);
  };

  const handleProjectStageChange = (selected) => {
    const projectStage = projectStages.find((item) => item?.stageId === selected?.id);
    setSelectedProjectStage(selected);
    setIncomeDetails((prev) => ({ ...prev, projectStageId: projectStage?.id || null }));
  };

  const handleAccountChange = (selected) => {
    setSelectedAccount(selected);
    setIncomeDetails((prev) => ({ ...prev, toAccountId: selected?.id || null }));
  };

  const handleCancelClick = () => {
    handleClearForm();
    navigate(-1);
  };

  const handleClearForm = () => {
    setErrors({});
    setIncomeDetails({
      amount: "",
      date: get_date(Date.now(), "YYYY-MM-DD"),
      categoryId: 1,
      description: undefined,
      projectId: null,
      projectStageId: undefined,
      sourceType: SourceEnum.clients,
      sourceId: undefined,
      status: "COMPLETED",
      toAccountId: selectedAccount?.id,
    });
    setSelectedClient(null);
    setSelectedProject(null);
    setSelectedProjectStage(null);
  };

  const handleSaveIncome = async () => {
    setErrors({});
    const errors = incomeValidationRules({ ...incomeDetails, incomeId });
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    const addEditIncomePayload = {
      ...incomeDetails,
      amount: Number(incomeDetails.amount),
      receivedAmount: Number(incomeDetails.amount),
      date: moment(`${incomeDetails.date} ${moment().format("HH:mm:ss")}`).toISOString(),
      isReceived: true,
      projectId: incomeDetails.projectId === "office" ? null : incomeDetails.projectId,
    };

    try {
      setLoading(true);
      if (incomeId) {
        const updateIncomePayload = {
          ...addEditIncomePayload,
          sourceType: undefined,
          sourceId: undefined,
          receivedAmount: undefined,
          toAccountId: undefined,
          isReceived: undefined,
        };
        await FinanceService.updateIncome(incomeId, updateIncomePayload);
      } else {
        await FinanceService.addIncome(addEditIncomePayload);
      }
      toast.success(`Income ${incomeId ? "updated" : "added"} successfully`);

      handleClearForm();
      if (clientId || incomeId || accountType === "office") {
        // A new record would be hidden by the filters the list had, so it opens fresh; after an edit they're kept.
        if (!incomeId) markTableReset();
        navigate(-1);
      } else if (projectId) navigate(`/projects/${projectId}?tab=income`);
    } catch (error) {
      let errorMessage = `Error ${incomeId ? "updating" : "adding"} income`;
      const response = error?.response?.data;

      if (Array.isArray(response?.message)) {
        errorMessage = response.message.join(", ");
      } else if (typeof response?.message === "string") {
        errorMessage = response.message;
      }

      toast.error(errorMessage);
      console.log("🚀 ~ handleSavePayment ~ error:", error);
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Box className="fl gap-2">
        <SelectField
          label="Client"
          value={selectedClient}
          getData={CRMService.getClientList}
          getDataStaticFilters={{ active: true, sortBy: "name", sortOrder: "asc" }}
          disabled={!!clientId || !!incomeId || !!projectId}
          required
          error={!!errors.sourceId}
          helperText={errors.sourceId}
          onChange={handleClientChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Project"
          value={selectedProject}
          defaultOption={hasSelectOfficeAccount ? { id: "office", name: "Company/Office" } : undefined} // TODO: replace this with a proper option for company/office project when available in the API
          getData={ProjectService.getProjectList}
          getDataStaticFilters={{ status: ["INPROGRESS"], clientId: selectedClient?.id, sortBy: "name", sortOrder: "asc" }}
          disabled={!!projectId || accountType === "office"}
          required
          error={!!errors.projectId}
          helperText={errors.projectId}
          onChange={handleProjectChange}
          style={{ width: "100%" }}
        />
        <SelectField
          label="Stage"
          value={selectedProjectStage}
          getData={ProjectService.getStages}
          getDataStaticFilters={stageFilter}
          disabled={stageIds.length === 0 || incomeDetails.projectId === "office"}
          error={!!errors.projectStageId}
          helperText={errors.projectStageId}
          onChange={handleProjectStageChange}
          style={{ width: "100%" }}
        />
      </Box>
      <Box className="fl gap-2">
        <InputField
          required
          label="Date"
          name="date"
          type="date"
          value={incomeDetails.date}
          onChange={updateDetailsState}
          error={!!errors.date}
          helperText={errors.date}
          fullWidth
          inputSlotProps={{
            htmlInput: {
              min: enableBackDate ? undefined : get_date(Date.now(), "YYYY-MM-DD"),
              onKeyDown: enableBackDate ? undefined : (e) => e.preventDefault(),
            },
          }}
        />
        <InputField
          label="Description"
          name="description"
          value={incomeDetails.description}
          onChange={updateDetailsState}
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
          autoComplete="on"
        />
        <InputField
          required
          label="Amount"
          name="amount"
          type="number"
          value={incomeDetails.amount}
          onChange={updateDetailsState}
          error={!!errors.amount}
          helperText={errors.amount}
          fullWidth
        />
      </Box>
      {!incomeId && (
        <Box className="fl gap-2">
          <SelectField
            label="Account"
            value={selectedAccount}
            getData={AccountService.getAdminAndUserAccounts}
            getDataStaticFilters={{ hasViewAccount, user: currentUser, companyAdminId: ownerUserId }}
            required
            error={!!errors.toAccountId}
            helperText={errors.toAccountId}
            onChange={handleAccountChange}
            style={{ width: "100%" }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <ActionButton buttonText="Cancel" onButtonClick={handleCancelClick} variant="outlined" />
        <ActionButton buttonText="Save" onButtonClick={handleSaveIncome} disabled={loading} />
      </Box>
    </>
  );
};
